// Исследовательский конвейер агрегаторов (work, мигр. 0060): агентные
// сессии на Mac mini ищут мерчантов агрегатора и их акции, сервер считает
// недобор. Раннер-поверхность (queue/result) ходит с X-Admin-Secret,
// человеческая поверхность (start/overview) — Firebase + dev cap.
import { z } from 'zod';
import { PlanCodeSchema } from '../vocab/plan';

/** scout — разведка агрегатора (Opus): план + кандидаты-мерчанты, ждёт аппрува;
 *  promos_batch — исполнитель по рецепту плана (Sonnet), пачка мерчантов;
 *  smoke — сквозная проверка обвязки (Haiku, без поиска);
 *  merchants/promos — v1, оставлены для старых кампаний. */
export const ResearchTaskKindSchema = z.enum(['scout', 'promos_batch', 'smoke', 'merchants', 'promos', 'manifest_qa']);
export type ResearchTaskKind = z.infer<typeof ResearchTaskKindSchema>;

/** plan_ready — разведка вернула план, кампания ждёт аппрува владельца. */
export const ResearchTaskStatusSchema = z.enum(['pending', 'running', 'plan_ready', 'done', 'failed', 'not_enough']);
export type ResearchTaskStatus = z.infer<typeof ResearchTaskStatusSchema>;

// ── POST /api/work/research/start (Firebase + dev cap) ─────────────────────
export const ResearchStartReqSchema = z.object({
  // Только разрешённые коды проекта (35, vocab/plan) — ADR-0061.
  locale: PlanCodeSchema,
  country: z.string().trim().max(60).default(''),
  category: z.string().trim().max(40).default('coupon'),
  aggregator: z.string().trim().min(1).max(160),
  /** Остаток лимита (сколько всего вариаций нужно). Если не передан —
   *  сервер берёт из зеркала locale_limits минус отгруженное по ячейке. */
  remaining: z.number().int().min(1).max(100000).optional(),
  /** Модальность ячейки лимита: screenshot (приложения/сайты) или photo
   *  (печатные купоны). Влияет только на авто-расчёт остатка. */
  modality: z.enum(['screenshot', 'photo']).default('screenshot'),
});
export type ResearchStartReq = z.infer<typeof ResearchStartReqSchema>;

export const ResearchStartResSchema = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  task_id: z.string().optional(),
  /** ceil(remaining / 20) — сколько мерчантов минимум. */
  needed_merchants: z.number().optional(),
  target_promos: z.number().optional(),
  existing: z.boolean().optional(),
});
export type ResearchStartRes = z.infer<typeof ResearchStartResSchema>;

// ── POST /api/work/research/queue (X-Admin-Secret; раннер Mac mini) ────────
export const ResearchQueueReqSchema = z.object({
  runner: z.string().trim().min(1).max(120),
});
export type ResearchQueueReq = z.infer<typeof ResearchQueueReqSchema>;

export const ResearchQueueTaskSchema = z.object({
  task_id: z.string(),
  kind: ResearchTaskKindSchema,
  locale: z.string(),
  country: z.string(),
  category: z.string(),
  aggregator: z.string(),
  merchant: z.string(),
  needed: z.number(),
  /** Имена уже известных мерчантов кампании — чтобы раннер не приносил их снова. */
  known_merchants: z.array(z.string()).default([]),
  /** kind=manifest_qa: JSON {card_id, r2_key, file_name, version, kind};
   *  kind=promos_batch: JSON {recipe, merchants:[{id,name,url}]}. */
  payload: z.string().default(''),
  /** Модель и лимит ходов для этой задачи — решает сервер по kind. */
  model: z.string().default('opus'),
  max_turns: z.number().int().default(40),
});
export const ResearchQueueResSchema = z.object({
  ok: z.boolean(),
  /** null = очередь пуста. */
  task: ResearchQueueTaskSchema.nullable(),
});
export type ResearchQueueRes = z.infer<typeof ResearchQueueResSchema>;

// ── POST /api/work/research/result (X-Admin-Secret) ────────────────────────
export const ResearchMerchantInSchema = z.object({
  name: z.string().trim().min(1).max(160),
  /** Имя на языке локали (для дедупа EN/漢字 и для дизайна). */
  name_local: z.string().trim().max(160).default(''),
  /** Страница мерчанта/офферов у агрегатора — точка входа для исполнителя. */
  url: z.string().trim().max(1000).default(''),
  evidence_url: z.string().trim().max(1000).default(''),
  note: z.string().max(500).default(''),
  /** Разведка: у мерчанта есть купоны/ваучеры/офферы, а не только баллы. */
  coupon_likely: z.boolean().default(true),
});

/** План разведки (kind=scout). */
export const ResearchPlanSchema = z.object({
  aggregator_type: z.string().max(200).default(''),
  /** good — купонный агрегатор; partial — купоны у части мерчантов; poor — баллы/не наш формат. */
  fit: z.enum(['good', 'partial', 'poor']),
  expected_coupon_merchants: z.number().int().min(0).default(0),
  /** Рецепт для исполнителя: где лежат офферы, как устроена страница, что считать акцией. */
  recipe: z.string().max(8000).default(''),
  data_sources: z.array(z.string().max(1000)).max(40).default([]),
  risks: z.string().max(2000).default(''),
});
export type ResearchPlan = z.infer<typeof ResearchPlanSchema>;

/** Результат исполнителя по одному мерчанту (kind=promos_batch). */
export const ResearchBatchItemSchema = z.object({
  merchant_id: z.string().trim().min(1).max(60),
  status: z.enum(['done', 'not_enough', 'no_coupons', 'failed']).default('done'),
  note: z.string().max(1000).default(''),
  promos: z.array(z.lazy(() => ResearchPromoInSchema)).max(50).default([]),
});
export type ResearchMerchantIn = z.infer<typeof ResearchMerchantInSchema>;

export const ResearchPromoInSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(2000).default(''),
  discount: z.string().max(120).default(''),
  image_url: z.string().max(1000).default(''),
  evidence_url: z.string().max(1000).default(''),
  valid_until: z.string().max(40).default(''),
  extra: z.record(z.string(), z.string()).optional(),
});
export type ResearchPromoIn = z.infer<typeof ResearchPromoInSchema>;

export const ResearchResultReqSchema = z.object({
  task_id: z.string().trim().min(1).max(60),
  runner: z.string().trim().max(120).default(''),
  status: z.enum(['done', 'failed', 'not_enough']),
  note: z.string().max(2000).default(''),
  merchants: z.array(ResearchMerchantInSchema).max(200).default([]),
  promos: z.array(ResearchPromoInSchema).max(50).default([]),
  /** kind=scout. */
  plan: ResearchPlanSchema.optional(),
  /** kind=promos_batch. */
  batch: z.array(ResearchBatchItemSchema).max(20).default([]),
  /** kind=manifest_qa: полный отчёт агента (JSON-строка) — уедет в R2. */
  report_json: z.string().max(4_000_000).default(''),
  /** kind=manifest_qa: исправленный агентом манифест (JSON-строка), '' = не чинил. */
  corrected_manifest: z.string().max(8_000_000).default(''),
  /** kind=manifest_qa: проблемы, требующие доработки СОТРУДНИКОМ. */
  issues: z.array(z.object({
    row_ref: z.string().max(200).default(''),
    problem: z.string().min(1).max(1000),
    fix_needed: z.string().max(1000).default(''),
  })).max(100).default([]),
});
export type ResearchResultReq = z.infer<typeof ResearchResultReqSchema>;

export const ResearchResultResSchema = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  accepted: z.number().optional(),
  /** Что сервер сделал дальше: spawned_promos / topup_merchants / campaign_done / campaign_not_enough / waiting. */
  next: z.string().optional(),
});
export type ResearchResultRes = z.infer<typeof ResearchResultResSchema>;

// ── GET /api/work/research/overview (Firebase + dev cap) ───────────────────
export const ResearchCampaignSchema = z.object({
  task_id: z.string(),
  status: ResearchTaskStatusSchema,
  locale: z.string(),
  country: z.string(),
  category: z.string(),
  aggregator: z.string(),
  round: z.number(),
  target_promos: z.number(),
  needed_merchants: z.number(),
  merchants_found: z.number(),
  promos_found: z.number(),
  open_tasks: z.number(),
  note: z.string(),
  created_at: z.string(),
});
export const ResearchOverviewResSchema = z.object({
  ok: z.boolean(),
  campaigns: z.array(ResearchCampaignSchema).default([]),
});
export type ResearchOverviewRes = z.infer<typeof ResearchOverviewResSchema>;

// ── POST /api/work/research/_admin/approve (X-Admin-Secret) ────────────────
// Аппрув плана разведки: из мерчантов статуса planned (или явного списка)
// режутся пачки исполнителю. Без аппрува исполнители не стартуют.
export const ResearchApproveReqSchema = z.object({
  task_id: z.string().trim().min(1).max(60),
  /** Подмножество research_merchants.id; пусто = все planned с coupon_likely. */
  merchant_ids: z.array(z.string().max(60)).max(500).default([]),
  batch_size: z.number().int().min(1).max(10).default(5),
});
export type ResearchApproveReq = z.infer<typeof ResearchApproveReqSchema>;
export const ResearchApproveResSchema = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  batches: z.number().optional(),
  merchants: z.number().optional(),
});
export type ResearchApproveRes = z.infer<typeof ResearchApproveResSchema>;

// ── POST /api/work/research/_admin/smoke (X-Admin-Secret) ──────────────────
// Заводит kind=smoke: Haiku, 3 хода, без поиска — проверка, что цепочка
// очередь → claude -p → парсер → result замкнута, ПРЕЖДЕ чем тратить Opus.
export const ResearchSmokeResSchema = z.object({ ok: z.boolean(), task_id: z.string() });
